import { useCallback, useEffect, useRef } from 'react';

import { ACTIVITY_THROTTLE_MS } from '@/config/sessionConfig';

interface UseIdleTimerOptions {
    /** Called when the user has been idle for (timeoutMs - warningMs). */
    onWarning: () => void;
    /** Called when the user has been idle for timeoutMs with no action taken. */
    onExpire: () => void;
    /** When false the timer is not started (e.g. user is not logged in). */
    enabled: boolean;
    /** How long before session expires (ms). Sourced from cache.frontend at the call-site. */
    timeoutMs: number;
    /** How long before expiry the warning fires (ms). Sourced from cache.frontend at the call-site. */
    warningMs: number;
}

/**
 * useIdleTimer
 *
 * Tracks user inactivity by listening to DOM activity events on the window.
 * Fires `onWarning` when the warning threshold is crossed and `onExpire` when
 * the full idle timeout elapses.
 *
 * Activity events are throttled (leading-edge) to ACTIVITY_THROTTLE_MS so the
 * timer is not reset on every pixel of mouse movement.
 *
 * Returns `resetTimer()` so that external code (e.g. the session manager) can
 * force-reset the clock on a confirmed user action such as clicking "Continue".
 */
export function useIdleTimer({
    onWarning,
    onExpire,
    enabled,
    timeoutMs,
    warningMs,
}: UseIdleTimerOptions) {
    const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const expireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastActivityRef = useRef<number>(Date.now());
    const throttleRef = useRef<boolean>(false);

    // Stable refs so the event listener never needs to be re-registered when
    // the caller's callbacks change identity across renders.
    const onWarningRef = useRef(onWarning);
    const onExpireRef = useRef(onExpire);
    useEffect(() => {
        onWarningRef.current = onWarning;
    }, [onWarning]);
    useEffect(() => {
        onExpireRef.current = onExpire;
    }, [onExpire]);

    const clearTimers = useCallback(() => {
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (expireTimerRef.current) {
            clearTimeout(expireTimerRef.current);
            expireTimerRef.current = null;
        }
    }, []);

    const startTimers = useCallback(() => {
        clearTimers();

        const warningDelay = timeoutMs - warningMs;

        warningTimerRef.current = setTimeout(() => {
            onWarningRef.current();
        }, warningDelay);

        expireTimerRef.current = setTimeout(() => {
            onExpireRef.current();
        }, timeoutMs);
    }, [clearTimers, timeoutMs, warningMs]);

    /** Reset the idle clock — call this when the user takes an explicit action. */
    const resetTimer = useCallback(() => {
        lastActivityRef.current = Date.now();
        if (enabled) {
            startTimers();
        }
    }, [enabled, startTimers]);

    // Activity handler with leading-edge throttle.
    const handleActivity = useCallback(() => {
        if (throttleRef.current) return;

        throttleRef.current = true;
        setTimeout(() => {
            throttleRef.current = false;
        }, ACTIVITY_THROTTLE_MS);

        lastActivityRef.current = Date.now();
        startTimers();
    }, [startTimers]);

    useEffect(() => {
        if (!enabled) {
            clearTimers();
            return;
        }

        // Start the clock immediately on mount / enable.
        startTimers();

        const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        events.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));

        return () => {
            events.forEach((evt) => window.removeEventListener(evt, handleActivity));
            clearTimers();
        };
    }, [enabled, handleActivity, startTimers, clearTimers]);

    return { resetTimer };
}
